import { useCallback, useRef } from 'react';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import NewOperationButton from 'components/airspace/NewOperationButton';
import SearchInput from 'components/airspace/SearchInput';

import useCurrentLocation from 'hooks/useCurrentLocation';
import useMobileViewport from 'hooks/useMobileViewport';

import useSearchDrawerDrag from './hooks/useSearchDrawerDrag';
import useSearchState from './hooks/useSearchState';

import QuickActionsRow from './components/QuickActionsRow';
import ResultsRegion from './components/ResultsRegion';
import { EYEBROW_LABEL, TEST_ID } from './constants';
import type { FunctionComponent, PickLocationParams, Props } from './types';

import styles from './styles.module.scss';

const noopSavedPlaces = (): void => console.log('Saved places: not yet implemented');

const SearchDrawer: FunctionComponent<Props> = ({
    className = '',
    onPickLocation,
    onRequestNewOperation,
    testId = TEST_ID
}) => {
    const { isMobile } = useMobileViewport();
    const dragHandleRef = useRef<HTMLDivElement | null>(null);
    const state = useSearchState();
    const { fetchCurrentLocation, isLoading: isLoadingCurrentLocation } = useCurrentLocation();

    useSearchDrawerDrag({
        dragHandleRef,
        isEnabled: isMobile,
        onCollapse: state.collapse,
        onExpand: state.expand
    });

    const handlePick = useCallback(
        (params: PickLocationParams) => onPickLocation(params),
        [onPickLocation]
    );

    const handleCurrentLocation = async () => {
        const coords = await fetchCurrentLocation();

        if (coords !== null) {
            handlePick(coords);
        }
    };

    const expandedClass = state.isExpanded ? styles.searchDrawerExpanded : '';
    const ctaSlotExpandedClass = state.isExpanded ? styles.ctaSlotExpanded : '';

    return (
        <VerticalContainer
            className={`${styles.searchDrawer} ${expandedClass} ${className}`}
            testId={testId}>
            <Wrapper className={styles.dragHandlePill} ref={dragHandleRef} />

            <HorizontalContainer className={styles.eyebrowRow}>
                <Wrapper className={styles.eyebrow} testId={`${testId}-eyebrow`}>
                    {EYEBROW_LABEL}
                </Wrapper>
            </HorizontalContainer>

            <Wrapper className={styles.inputSlot}>
                <SearchInput
                    onChange={state.setQuery}
                    testId={`${testId}-input`}
                    value={state.query}
                />
            </Wrapper>

            <QuickActionsRow
                isLoadingCurrentLocation={isLoadingCurrentLocation}
                onCurrentLocation={handleCurrentLocation}
                onSavedPlaces={noopSavedPlaces}
                parentTestId={testId}
            />

            <ResultsRegion
                isExpanded={state.isExpanded}
                onPick={handlePick}
                parentTestId={testId}
                query={state.query}
                recents={state.recents}
                recordRecent={state.recordRecent}
                results={state.results}
            />

            <Wrapper className={`${styles.ctaSlot} ${ctaSlotExpandedClass}`}>
                <NewOperationButton onClick={onRequestNewOperation} />
            </Wrapper>
        </VerticalContainer>
    );
};

export default SearchDrawer;
